<template>
  <VanPopup
    :show="visible"
    position="bottom"
    :style="{ height: '80vh', background: 'transparent' }"
    @close="handlePopupClose"
  >
    <div
      v-if="skuActivateInfo && showCountdownCard"
      class="activate-card"
    >
      <ActivateCountdownCard
        :key="activityKey(skuActivateInfo)"
        @countdown:over="emits('activity:over', $event)"
        :options="{
          queryTime: skuActivateInfo.queryTime,
          endTime: skuActivateInfo.endTime,
          startTime: skuActivateInfo.startTime
        }"
        :is-small="true"
        :activityInfo="skuActivateInfo"
      />
    </div>
    <div class="sku-wrapper" :activate="skuActivateInfo && showCountdownCard ? '1' : '0'">
      <van-icon name="close" class="close-btn" @click="handlePopupClose" />
      
      <div class="goods-info">
        <div
          class="goods-img-box"
          @click="handleSkuImageClick"
        >
          <img class="goods-img" v-lazy="$cvWebpFn(currentSelectedSku?.images[0] || useImg1pxBase64().value)">
        </div>
        <div class="goods-inner-info">
          <div class="desc">
            <div class="price">
              {{ $currencySymbol() }}
              {{ $toThousandsFn(skuActivateInfo?.activityPrice || skuPrice.salePrice) }}
            </div>
            <div
              v-show="skuPrice.salePrice !== skuPrice.listPrice"
              class="underline-price"
            >
              {{  $currencySymbol() }}{{  $toThousandsFn(skuPrice.listPrice) }}
            </div>
          </div>

          <div class="stock-state">
            <span class="stock-state-text">{{ stockStateText }}</span>

            <VanStepper
              v-model="quantity"
              integer
              @change="handleQtyChange"
            />
          </div>
        </div>
      </div>
      
      <div class="sku-inner-wrapper">
        <button v-if="inventoryStatus === 'error'" @click="emits('retry:inventory')">Unable to refresh stock. Retry</button>
        <div class="card-box">
          <div class="gifts-card receiving-address" @click="handleSelectAddressClick">
            <div class="gifts-title">
              <span class="title-text">Shipping To</span>
              <span class="icon">Select&nbsp;<van-icon name="arrow" /></span>
            </div>
            
            <div class="address-detail">
              <span class="icon"><van-icon color="#DD3131" name="location-o"/></span>
              
              <div v-if="!userAddressLoading" class="detail-desc">{{ addressDetail }}</div>
              <div v-else class="detail-desc" style="text-align: center;">
                <van-loading class="address-loading" size="18" type="spinner">Loading...</van-loading>
              </div>
            </div>
          </div>

          <div class="gifts-card" v-if="deliveryInfo?.logisticsType">
            <DeliveryCard :delivery="deliveryInfo" concise />
          </div>

          <div class="gifts-card" v-if="gifts.length > 0">
            <div class="gifts-title">Gifts</div>
            <KGiftsCard :gifts="gifts" />
          </div>
        </div>

        <div class="specs-info-box">
          <div
            v-for="(item, idx) in detail.specs"
            :key="idx"
            class="specs-info-item"
          >
            <div class="label info-item-spec">
              <span>{{ item.name }}</span>
              <span
                v-if="item.name?.toLocaleLowerCase().indexOf('size') >= 0 && detail.sizeChartPicture"
                class="size-guide"
                @click="handleSizeGuideClick(detail.sizeChartPicture)"
              >
                Size Guide
                <van-icon name="arrow" />
              </span>
            </div>
            <div class="spec-value">
              <div
                v-for="(specValue, specIdx) in item.values"
                :key="`${idx}_${specIdx}`"
                class="spec-btn-box"
                v-show="!specValue.disabledRemoved"
              >
                <div
                  :class="detail.specs.length <= 1 ? 'sku-btn big-sku-btn' : 'sku-btn'"
                  :select="specSelectIndexs[idx] === `${idx}_${specIdx}` && '1'"
                  @click="handleSkuBtnClick(idx, specIdx)"
                >
                  <div class="sku-img-box" v-if="specValue.image">
                    <img
                      v-lazy="$cvWebpFn(specValue.image, { w: 80, removeTag: true })"
                      class="sku-img"
                    />
                  </div>
                  <span class="sku-value-text">{{ specValue.value }}</span>
                </div>

                <ClientOnly>
                  <div v-if="specValue.disabled || specValue.temporaryDisabled" class="sold-out-mark">Sold out</div>
                </ClientOnly>
              </div>
            </div>
          </div>
        </div>
      </div>

      <div class="option-box" v-if="showArrivalReminderBtn">
        <!-- 到货通知订阅 -->
        <button
          type="button"
          id="buyNow"
          :class="currentSelectedSku?.isArrivalReminder ? 'opt-btn red-btn disabled-opt-btn' : 'opt-btn red-btn'"
          @click="handleArrivalReminderClick"
        >
          {{ currentSelectedSku?.isArrivalReminder ? 'Cancel Reminder' : 'Arrival Reminder' }}
        </button>
      </div>
      <template v-else>
        <div class="option-box" v-if="visibleType !== 'switch-sku'">
          <button
            type="button"
            id="buyNow"
            :class="
              confirmDisabled
                ? 'opt-btn red-btn disabled-opt-btn'
                : 'opt-btn red-btn'
            "
            :disabled="confirmDisabled"
            @click="handleConfirmClick"
          >
            {{ buyNowText.btnText }}
          </button>
        </div>

        <ClientOnly v-else-if="isExtremeDiversion">
          <div class="option-box">
            <a
              :href="purchaseDeepLinkUrl"
              class="opt-btn red-btn app-only-btn"
              @click="handleOpenAppClick"
            >
              <span class="app-only-text">Purchase Only In Kilimall APP</span>
              <van-icon name="arrow" class="app-only-arrow" />
            </a>
          </div>
        </ClientOnly>

        <div class="option-box switch-sku-option-box" v-else>
          <button v-if="!isCps"
            :class="
              !actions.cart || userAddressLoading
                ? 'opt-btn add-car-btn disabled-opt-btn'
                : 'opt-btn add-car-btn'
            "
            :disabled="!actions.cart || userAddressLoading"
            @click="handleAddCartClick('Add To Cart')"
          >Add To Cart</button>
          <button 
            :class="
              optionBtnState
                ? 'opt-btn red-btn disabled-opt-btn'
                : 'opt-btn red-btn'
            "
            :disabled="optionBtnState || userAddressLoading"
            @click="handleOrderNowClick('Order Now')"
          >Order Now</button>
        </div>
      </template>
    </div>
  </VanPopup>
</template>
  
<script setup lang="ts">
import { purchaseActions, activityKey, preserveOriginalAttribution, type InventoryStatus } from '../purchase'
import { getCpsCandidate } from '@/utils/cps-checkout'
import { type IGifts } from '@/components/KGifts/Card.vue'
import { addCartItem, putSubscription, type ISubscriptionParams,type ISkuItemParams } from '@/api/models/product'
import { getShipingCartTotal } from '@/api/models/userInfo'
import { getShowArrivalReminder, LazyQueue, tiktokTrack, type ITrackConfig } from '../mixins';
import { trackFn, getPromotionId } from '../mixins';
import ActivateCountdownCard from './ActivateCountdownCard.vue'
import DeliveryCard from './DeliveryCard.vue'
import { customEventFn, eventName, webClickFn } from '@/plugins/bi-sa.client'
import { config as sensorsConfig } from '@/plugins/sensors.client'
import { setUrlCache } from '~/api/models/system';
import { getTrafficSourceParts } from '@/utils/traffic-source'
import { useWapListingDetailVersionState } from '@/utils/wap-listing-detail-version'

const queue = new LazyQueue()
interface IProps {
  quantity?: number
  inventoryStatus?: InventoryStatus
  isCps?: boolean
  visible: boolean,
  userAddressLoading: boolean,
  visibleType: ProductV2.IOptionType,
  bigPromotion: ProductV2.IBigPromotion[]
  detail: ProductV3.ListingDetailV3
  gifts: IGifts[]
  skus: ProductV3.Sku[]
  skuPrice: ProductV2.ISkuPrice
  // currentSkuService?: ProductV2.ISkuService
  store?: Store.IStore
  skuActivateInfo?: ProductV3.SelectedSkuActivity
  currentSelectedSku?: ProductV3.Sku
  addressId?: number
  address?: Address.IAddressItem 
  trackBaseData: ITrackConfig
  deliveryInfo?: ProductV3.ShippingTime
}
interface IEmits {
  (event: 'update:visible', visible: boolean): void
  (event: 'update:sku', key: string, sku: ProductV3.Sku | undefined): void
  (event: 'update:sku-spec', skuSpec: string): void
  (event: 'update:skuKey', specKey: string): void
  (event: 'arrival-reminder', state: boolean): void

  (event: 'activity:over', activity: ProductV3.SelectedSkuActivity): void
  (event: 'retry:inventory'): void
  (event: 'add-cart:success'): void
  (event: 'delivery:warehouse', data: any): void
  (event: 'update:ok', data: any): void
  (event: 'select:address'):void
  (event: 'update:quantity', val: number):void
}
const props = withDefaults(defineProps<IProps>(), { visible: false })
const emits = defineEmits<IEmits>()
const { $SENSORS_EVENT, $setShoppingCartLaconicList, $getCountry, $getListingDetailHref, $sensors } = useNuxtApp()
const wapListingDetailVersion = useWapListingDetailVersionState()
const isExtremeDiversion = computed(() => !props.isCps && wapListingDetailVersion.value === '2')
const { getDeepLinkUrl, handleOpenAppClick } = useProductDeepLink(
  () => props.currentSelectedSku?.skuId || props.detail.listingId,
  () => props.skuActivateInfo?.activityType === 1 ? 1 : 0,
  () => ({
    source: props.trackBaseData.source,
    trafficSourceMeta: props.trackBaseData.trafficSourceMeta,
  }),
)
const purchaseDeepLinkUrl = getDeepLinkUrl('purchase')
const router = useRouter()
const route = useRoute()
const quantity = ref<number>(props.quantity ?? 1)
watch(() => props.quantity, value => { if (value !== undefined && value !== quantity.value) quantity.value = value })
const specSelectIndexs = ref<string[]>([])

const showCountdownCard = computed(() => props.skuActivateInfo && [1, 4, 6].includes(props.skuActivateInfo?.activityType))
const addressDetail = computed<string>(() => {
  if(!props.address) { return 'Please Choose receiving address.' }

  const { areaNameList,address } = props.address

  const citys = areaNameList.map(c => c.name).join(' ')
  return `${citys} ${address}`
})

const maxQuantity = computed<number>(() => {
  if(!props.currentSelectedSku) {
    return 0
  }

  const { fbkAvailable, dsAvailable, gsAvailable, fbkInventories } = props.currentSelectedSku.inventory

  if(fbkAvailable > 0) {
    return Math.max(...fbkInventories.map(c => c.available))
  }

  return Math.max(dsAvailable, gsAvailable)
})

const stockStateText = computed<string>(() => {
  const { currentSelectedSku, skuActivateInfo } = props

  if(!currentSelectedSku) { return 'Out of Stock' }

  const { fbkInventories, gsAvailable, fbkAvailable, dsAvailable } = currentSelectedSku.inventory

  let maxStock = Math.max(...fbkInventories.map(c => c.available))
  if(maxStock <= 0) {
    maxStock = Math.max(gsAvailable, Math.max(...fbkInventories.map(c => c.available)), dsAvailable)
  }

  if(skuActivateInfo?.activityType === 1) {
    const {gsAvailable: flashGs, fbkAvailable: flashFbk, dsAvailable: flashDs} = skuActivateInfo.flashSale.inventory
    const flashMaxStock = Math.max(flashGs, flashFbk, flashDs)

    if(flashMaxStock <= 0) return 'Flash sales Out of Stock'
    if(flashMaxStock <= 5) return 'Flash sales Low Stock'
    return 'Flash sales In Stock'
  } else {
    if(maxStock <= 0) return 'Out of Stock'
    if(maxStock <= 5) return 'Low Stock'
    return 'In Stock'
  }
})
// 操作按钮是否可用（加入购物车、下单）
const actions = computed(() => purchaseActions({
  sku: props.currentSelectedSku, activity: props.skuActivateInfo, store: props.store,
  quantity: quantity.value, inventoryStatus: props.inventoryStatus, platform: 'wap',
  allowOriginalPrice: props.visibleType === 'order-now'
}))
const optionBtnState = computed(() => actions.value.buy === 'blocked')
const confirmDisabled = computed(() => props.userAddressLoading || (props.visibleType === 'add-to-cart' ? !actions.value.cart : optionBtnState.value))

// 订阅到货通知按钮显示
const showArrivalReminderBtn = computed(() => {
  const { currentSelectedSku, skuActivateInfo, store } = props
  
  return getShowArrivalReminder(currentSelectedSku, skuActivateInfo, store)
})

const buyNowText = computed(() => ({
  btnText: actions.value.reason === 'loading' ? 'Loading...' : actions.value.buy === 'original-price' ? 'Buy at Original Price' : confirmDisabled.value ? 'Unavailable' : 'Ok',
  btnTips: actions.value.buy === 'original-price' ? 'This flash sales product is out of stock right now.' : ''
}))

const stepperMaxVal = computed<number>(() => {
  const { skuActivateInfo } = props
  const defaultLimitNum = $getCountry()?.orderConfig?.qtyLimit || 30
  

  // 秒杀只能 加购/购买 限定数量的商品
  if(skuActivateInfo?.activityType === 1) {
    const limitNum = skuActivateInfo.flashSale?.limitNum

    const { gsAvailable, fbkAvailable, dsAvailable } = skuActivateInfo.flashSale.inventory
    const maxStock = Math.max(gsAvailable, fbkAvailable, dsAvailable)
    return limitNum && limitNum <= 0 ?  (maxStock > defaultLimitNum ? defaultLimitNum : maxStock) : limitNum
  } else {
    // 普通商品默认最多购买和加购 配置的最大可购买数量的 商品
    return maxQuantity.value > defaultLimitNum ? defaultLimitNum : maxQuantity.value
  }
})
const handlePopupClose = () => {
  emits('update:visible', false)
}

const showPreviewImage = (src: string) => {
  handlePopupClose()
  showImagePreview({
    images: [src],
    closeable: true,
    onClose: () => {
      emits('update:visible', true)
    }
  });
}

const handleSkuImageClick = () => {
  const src = props.currentSelectedSku?.images[0]
  src && showPreviewImage(src)
}

const handleConfirmClick = async () => {  
  if(props.visibleType === 'add-to-cart') {
    await handleAddCartClick('Ok')
  }

  if(props.visibleType === 'order-now') {
    await handleOrderNowClick('Ok')
  }
}

// 到货通知订阅
const handleArrivalReminderClick = async() => {
  if(!props.currentSelectedSku) {
    return
  }
  const { isArrivalReminder, skuId } = props.currentSelectedSku

  try {
    await showConfirmDialog({
      message: isArrivalReminder 
        ? 'Do you want to cancel your subscription?'
        : 'Do you want to confirm that "Arrival Reminder" is enabled? Enable this function to receive alert when your desired products are restocked.',
    })
  } catch(e) {
    return
  }
  
  const params: ISubscriptionParams = {
    source_type: 3,
    is_subscribe: !isArrivalReminder,
    sku_id: skuId
  }

  try {
    await putSubscription(params)
    showToast('Notification set successfully!')

    emits('arrival-reminder', params.is_subscribe)
  } catch(e: any) {
    console.error(e)
    if(e.code !== 1001 && e.msg) {
      showToast(e.msg)
    }
  }
}

const handleSelectAddressClick = () => {
  emits('select:address')
}
const handleSizeGuideClick = (sizeChartPicture: string) => {
  showPreviewImage(sizeChartPicture)
}

const handleAddCartClick = async(btnName: string) => {
  if(props.userAddressLoading || !actions.value.cart) {
    return
  }

  if(!props.currentSelectedSku) {
    return showToast('Please choose a specification.')
  }
  if(!useUserInfo().value?.accessToken) {
    return router.push(`/login?redirect=${encodeURI(route.fullPath)}`)
  }

  try {
    const { articleId, authorCode, referCode } = route.query || {}
    const source = props.trackBaseData.source || ''
    const currentSkuId = props.currentSelectedSku!.skuId
    const [
      internalLatestTrafficSourceModule = '',
      internalLatestTrafficSourceType = '',
      insideLatestTrafficSourceValue = '',
      internalFirstTrafficSourcePage = '',
      internalLatestTrafficSourcePage = ''
    ] = getTrafficSourceParts(source, props.trackBaseData.trafficSourceMeta)
    const isAd = props.trackBaseData.isAd || false
    const isNewTag = !!props.trackBaseData.isNewTag
    const isChoiceTag = !!props.trackBaseData.isChoiceTag
    const requestId = props.trackBaseData.requestId || ''
    const algorithmModelName = props.trackBaseData.algorithmModelName || ''
    const skuParams: ISkuItemParams = {
      sku: currentSkuId,
      count: quantity.value,
      activityId: 0,
      activityType: 0,
      referCode,
      authorCode,
      referSource: 'Listing',
      referSourceId: articleId,
      internalLatestTrafficSourceModule,
      internalLatestTrafficSourceType,
      insideLatestTrafficSourceValue,
      internalFirstTrafficSourcePage,
      internalLatestTrafficSourcePage,
      isAd,
      isNewTag,
      isChoiceTag,
      requestId,
      algorithmModelName
    }

    const addCartRes = await addCartItem(skuParams)
    if (addCartRes.code === 0) {
      showToast('Successfully added to shopping cart.')
      // BI埋点
      webClickFn(101, btnName, true)
    } else {
      // BI埋点
      webClickFn(101, btnName, false, 'api_code_error', addCartRes.msg || addCartRes.message)
    }
    const { imageUrls, listingId: id, title } = props.detail

    $setShoppingCartLaconicList({
      listingId: id,
      skuId: currentSkuId || -1,
      productImage: imageUrls[0] || '',
      productTitle: title,
      spec: props?.currentSelectedSku?.spec || '',
      salePrice: props.skuPrice.salePrice
    })

    // 加购埋点
    queue.addTask(() => { trackFn(props.trackBaseData, $SENSORS_EVENT.addToCart) })
    queue.addTask(() => {
      tiktokTrack(
        'AddToCart',
        quantity.value,
        props.currentSelectedSku,
        props.detail
      )
    })
    getShipingCartTotal().then((r: any) => useShopingCartTotal().value = r?.count || 0)

    handlePopupClose()
  } catch (e) {
    console.error(e)
  }
}

const handleQtyChange = (e: number) => {
  let currentQty = e
  
  if(e > stepperMaxVal.value) {
    currentQty = stepperMaxVal.value
    if(props.skuActivateInfo?.activityType === 1) {
      showToast(`You can only place a maximum of ${stepperMaxVal.value} flash sales item per order.`)
    } else {
      showToast(`We've adjusted the quantity to ${stepperMaxVal.value}.`)
    }
  } else if(e < 1) {
    currentQty = 1
  } else {
    currentQty = e
  }
  
  quantity.value = currentQty
  if (currentQty !== props.quantity) emits('update:quantity', currentQty)
}
const handleOrderNowClick = (btnName: string) => {
  const {
    currentSelectedSku, skuActivateInfo, addressId,
    bigPromotion, skuPrice, store, visibleType
  } = props
  
  if(optionBtnState.value) {
    $sensors.track(
      $SENSORS_EVENT.clickOrderNow,
      { verificationStatus: false, failureType: store?.status !== 1 ? 'Store' : 'Product', failureReason: 'The store is closed or has no stock or insufficient stock.' }
    )
    webClickFn(201, 'Order Now', false, store?.status !== 1 ? 'Store' : 'Product', 'The store is closed or has no stock or insufficient stock.') // warningTipsText.value.join(','))
    return
  }

  if(props.userAddressLoading) {
    $sensors.track(
      $SENSORS_EVENT.clickOrderNow,
      { verificationStatus: false, failureType: 'Address', failureReason: 'Loading used address.' }
    )
    webClickFn(201, btnName, false, 'page_check', 'Loading used address.')
    return
  }

  if(!useUserInfo().value?.accessToken) {
    $sensors.track($SENSORS_EVENT.clickOrderNow, { verificationStatus: false, failureType: 'NotLogin', failureReason: 'Not Login.' })
    webClickFn(201, btnName, false, 'page_check', 'Not Login.')

    if (route.query.cpsShareAsset) {
      return router.push({ path: '/login', query: { redirect: route.fullPath } })
    }
    return router.push(`/login?redirect=${encodeURI(route.fullPath)}`)
  }
  if(!currentSelectedSku) {
    $sensors.track($SENSORS_EVENT.clickOrderNow, { verificationStatus: false, failureType: 'Product', failureReason: 'Please choose a specification.' })
    webClickFn(201, btnName, false, 'page_check', 'Please choose a specification.')
    return showToast('Please choose a specification.')
  }

  if(!addressId) {
    $sensors.track($SENSORS_EVENT.clickOrderNow, { verificationStatus: false, failureType: 'Address', failureReason: 'Please choose a address.' })
    webClickFn(201, btnName, false, 'page_check', 'Please choose a address.')
    if (route.query.cpsShareAsset) {
      return router.push({ path: '/address/add', query: { redirect: route.fullPath } })
    }
    return router.push(`/address/add?redirect=${encodeURI(route.fullPath)}`)
  }

  if(actions.value.buy === 'original-price') {
    const { source = '' } = route.query

    const { url, key, cookie } = preserveOriginalAttribution($getListingDetailHref(
      currentSelectedSku.skuId,
      {
        type: 'sku',
        shortTitle: props.detail?.shortTitle || '',
        title: String(props.detail?.title || ''),
        source: String(source || '')
      },
      { writeCache: false }
    ), route.query)

    setUrlCache(cookie, key).then(() => {
      window.location.replace(url)
    })
    return
  }
  const promotionId = getPromotionId(skuActivateInfo?.activityPrice || skuPrice.salePrice, quantity.value, bigPromotion)

  const { authorCode, referCode } = route.query || {}
  const source = props.trackBaseData.source || ''

  const [
    internalLatestTrafficSourceModule = '',
    internalLatestTrafficSourceType = '',
    insideLatestTrafficSourceValue = '',
    internalFirstTrafficSourcePage = '',
    internalLatestTrafficSourcePage = ''
  ] = getTrafficSourceParts(source, props.trackBaseData.trafficSourceMeta)
  const activateParams: IAnyObject = { activityId: 0, activityType: 0 }

  // 只有秒杀商品才需要传入 活动ID 和 Type
  if(skuActivateInfo?.activityType === 1) {
    activateParams.activityId = skuActivateInfo?.activityId,
    activateParams.activityType = skuActivateInfo?.activityType
  }

  const byNow: Order.ICheckoutParam = {
    product: [{
      /** 埋点数据 */
      internalLatestTrafficSourceModule,
      internalLatestTrafficSourceType,
      insideLatestTrafficSourceValue,
      internalFirstTrafficSourcePage,
      internalLatestTrafficSourcePage,
      ...props.trackBaseData,
      /** 埋点数据 */
      promotionId: promotionId !== -1 ? promotionId : undefined,
      skuId: currentSelectedSku.skuId,
      count: quantity.value,
      // 外部分享，Prefee 老逻辑
      authorCode,
      referCode,
      // 活动 SKU 下单参数
      ...activateParams
    }],
    addressId: addressId,
    source: 2
  }
  const cpsCandidate = getCpsCandidate(route.query.cpsShareAsset, props.detail.cpsPathToken)
  if (cpsCandidate) byNow.product[0].cpsCheckoutCandidate = cpsCandidate
  localStorage.setItem('ck_pms', JSON.stringify(byNow))

  webClickFn(201, btnName, true)
  const categoryId = props.detail.categoriesPath[props.detail.categoriesPath.length - 1]?.id || ''
  
  router.push(`/checkout?addressId=${addressId}&categoryId=${categoryId}`)
}

const initSpecSelectIndexs = (specs: ProductV3.Specs[]) => {

  if(specs.length <= 0 || props.skus.filter(sku => sku.status > 0).length <= 0) { 
    if(specs.length > 0) {
      emits('update:sku-spec', specs.map(c => c.name).join('/'))
    }
    return
  }

  const skuSpecs: { name: string, id: string }[] = []
  if(props.currentSelectedSku) {
    const { key } = props.currentSelectedSku
    // const key = '4136:1599665;5657:1000000586'
    // 将 key 转换为 Map,方便按 pid 查找对应的 pvid
    const keyMap = new Map<number, number>()
    key.split(';').forEach((spec: string) => {
      const [specBase, specVal] = spec.split(':')
      keyMap.set(Number(specBase), Number(specVal))
    })
    
    // 根据 specs 数组的顺序来构建 specAry,确保顺序与 specs 一致
    specs.forEach((spec, baseIdx: number) => {
      const { values, name, pid } = spec
      const specVal = keyMap.get(pid)
      
      if(specVal !== undefined) {
        const valIdx = values.findIndex((c) => c.pvid === specVal)

        specSelectIndexs.value[baseIdx] = `${baseIdx}_${valIdx}`
        
        if(valIdx >= 0) {
          skuSpecs.push({ name: `${name}:${values[valIdx].value}`, id: `${pid}:${values[valIdx].pvid}` })
        }
      }
    })
  } else {
    specSelectIndexs.value = new Array(specs.length).fill('').map((v: any, idx: number) => `${idx}_0`)
    console.log(7777, specSelectIndexs.value)
    for(let i=0; i < specs.length; i++) {
      const { name, values, pid } = specs[i]

      if(values.length > 0) {
        // skuSpecs.push(`${name}:${values[0].value}`)
        skuSpecs.push({ name: `${name}:${values[0].value}`, id: `${pid}:${values[0].pvid}` })
      }
    }
  }

  const specName = skuSpecs.map(c => c.name).join('/')
  const skuKey = skuSpecs.map(c => c.id).join(';')
  emits('update:sku-spec', specName)
  emits('update:skuKey', skuKey)
}
watch(() => props.detail.specs, initSpecSelectIndexs)
onMounted(() => {
  initSpecSelectIndexs(props.detail.specs)
})
const handleSkuBtnClick = (index: number, idx: number) => {
  specSelectIndexs.value[index] = `${index}_${idx}`

  const skuKeys: string[] = []
  const skuSpecs: string[] = []
  specSelectIndexs.value.forEach((c: string) => {
    const [baseIdx, valIdx] = c.split('_').map((c: string) => Number(c))
    
    const spec = props.detail.specs[baseIdx]
    const specValue = spec.values[valIdx]
    skuKeys.push(`${spec.pid}:${specValue.pvid}`)

    skuSpecs.push(`${spec.name}:${specValue.value}`)
  })

  const sku = props.skus.find((c: ProductV3.Sku) => {
    let inSku = true
    for(let i=0; i < skuKeys.length; i++) {
      inSku = c.key.indexOf(skuKeys[i]) >= 0

      if(!inSku) { break }
    }

    return inSku
  })

  emits('update:sku-spec', skuSpecs.join('/'))
  // emits('update:skuKey', skuKeys.join(';'))
  emits('update:sku', skuKeys.join(';'), sku)

  // 切换sku增加BI商品浏览埋点
  if (sku) {
    const { source = '' } = route.query || {}
    const [
      internalLatestTrafficSourceModule = '',
      internalLatestTrafficSourceType = '',
      insideLatestTrafficSourceValue = '',
      internalFirstTrafficSourcePage = '',
      internalLatestTrafficSourcePage = ''
    ] = getTrafficSourceParts(source as string, props.trackBaseData.trafficSourceMeta)
    
    const { categoriesPath, listingId, title } = props.detail

    const categoryId = categoriesPath[categoriesPath.length - 1]?.id
    const biEventData = {
      internal_latest_traffic_source_module: internalLatestTrafficSourceModule,
      internal_latest_traffic_source_type: internalLatestTrafficSourceType,
      inside_latest_traffic_source_value: insideLatestTrafficSourceValue,
      internal_first_traffic_source_page: internalFirstTrafficSourcePage,
      internal_latest_traffic_source_page: internalLatestTrafficSourcePage,
      listing_id: listingId,
      sku_id: sku.skuId,
      category_id: categoryId,
      store_id: props.store?.id,
      isAd: props.trackBaseData.isAd,
      is_new_tag: !!props.trackBaseData.isNewTag,
      is_choice_tag: !!props.trackBaseData.isChoiceTag,
      request_id: props.trackBaseData.requestId || '',
      algorithm_model_name: props.trackBaseData.algorithmModelName || '',
      product_title: title,
      product_price: props.skuPrice.salePrice,
      product_inventory_type: props.deliveryInfo?.logisticsType?.toUpperCase(),
      sensor_properties: { ...props.trackBaseData }
    }
    
    const biProperties = {
      sensor_properties: { ...sensorsConfig.customEventCommonAttr }
    }
    
    customEventFn(eventName.viewProductDetail, biEventData, biProperties)
  }
}
</script>
  
<style lang="scss" scoped>
.sku-wrapper {
  height: 100%;
  background-color: #fff;
  position: relative;
  .sku-inner-wrapper {
    padding: 20px;
    overflow-y: auto;
    height: calc(100% - 100px - 200px);
    &::-webkit-scrollbar {
      display: none; /* Chrome Safari */
    }
  }
  
  .close-btn{
    position: absolute;
    top: 30px;
    right: 30px;
    color: #999;
    font-size: 46px;
  }
}
.sku-wrapper[activate="1"] {
  height: calc(100% - 80px);
}
.storeTips {
  padding-top: 26px;
  color: #999;
  font-size: 24px;
}
// bottom options
.option-box {
  box-shadow: 0 -1px 1px 0 rgba(211, 214, 215, 0.38);
  height: 76px;
  position: absolute;
  bottom: 20px;
  left: 20px;
  right: 20px;
  text-align: center;
  .opt-btn {
    line-height: 76px;
    color: #fff;
    border-radius: 40px;
    font-size: 28px;
    width: 100%;
    height: 100%;
    border: none;
    outline: none;
    font-weight: 700;
    background: white;
  }
  .disabled-opt-btn {
    color: #fff !important;
    line-height: 72px;
    border: 1px solid transparent !important;
    box-sizing: border-box;
    background: gray !important;
  }
  // .yellow-btn{
  //   background: linear-gradient(90deg,#ffbb01,#ff6247 100%,#ff6047 0);
  // }
  .add-car-btn{
    color: var(--default_color);
  }
  .red-btn{
    background-color: #e04545;
  }
  .app-only-btn{
    display: flex;
    align-items: center;
    justify-content: center;
    text-decoration: none;
    gap: 4px;
    .app-only-text{
      line-height: 1;
      font-size: 26px;
    }
    .app-only-arrow{
      font-size: 28px;
      line-height: 1;
    }
  }
}
.switch-sku-option-box{
  border: 3px solid var(--default_color);
  border-radius: 76px;
  display: flex;
  overflow: hidden;
  .opt-btn{
    border-top-left-radius: 0;
    border-bottom-left-radius: 0;
  }
  .disabled-opt-btn{
    border-radius: 0;
  }
}
.switch-sku-option-box[disabled="true"] { border-color: transparent;}

// sku info style
.goods-info {
  display: flex;
  padding: 20px 20px 0 20px;
  .goods-img-box {
    height: 160px;
    width: 160px;
    position: relative;
    border: 1px solid #efefef;
    border-radius: 8px;
    padding: 8px;
    .goods-img {
      width: 100%;
      height: 100%;
      border-radius: 8px;
      object-fit: contain;
    }
    .enlarge-img {
      position: absolute;
      top: 0;
      right: 0;
      width: 40px;
      height: 40px;
    }
  }
  .goods-inner-info {
    flex: 1;
    display: flex;
    flex-direction: column;
    justify-content: space-between;
    padding: 10px 0 10px 20px;
    font-size: 32px;
    .desc {
      display: flex;
      align-items: end;
      padding-top: 20px;
      .price {
        color: #ff2720;
        font-weight: 600;
      }
      .underline-price {
        margin-left: 20px;
        color: #999;
        font-size: 24px;
        text-decoration: line-through;
      }
    }
    .stock-state {
      display: flex;
      justify-content: space-between;
      align-items: center;
      
      .stock-state-text{
        color: #cacaca;
        font-size: 24px;
      }
      .van-stepper {
        :deep() .van-stepper__plus::after  {
          width: 3px;
        }
      }
    }
  }
}

.warning-tips {
  // padding-top: 26px;
  color: #999;
  font-size: 24px;
}

.address-detail{
  display: flex;
  align-items: center;
  color: #666;
  margin-top: 20px;
  .icon{
    min-width: 40px;
    i{ font-size: 40px; }
  }
  .address-loading{
    font-size: 24px;
  }
  .detail-desc{
    font-size: 24px;
    line-height: 28px;
    margin: 0 10px;
    flex: 1;
    @include ellipsisMultiline(3);
  }
}
.gifts-card{
  padding: 20px 0;
  min-height: 40px;
  border-bottom: 1px solid #efefef;
  &:last-child{border-bottom: none;}
  .gifts-title{
    font-size: 28px;
    font-weight: 600;
    margin: 10px 0;
    display: flex;
    align-items: center;
    color: #333;
    .title-text{
      flex: 1;
    }
  }
}

.specs-info-box{
  margin: 10px 0;
  .specs-info-item{
    margin-bottom: 40px;
    .label { color: #333; font-weight: 600;font-size: 28px;}
    .info-item-spec{
      display: flex;align-items: center;justify-content: space-between;
      .size-guide{ color: #3b9dff;font-weight: 400; }
    }
    .spec-value{
      .spec-btn-box{
        margin: 15px 15px 0 0;
        display: inline-block;
        position: relative;
      }
      
      .sku-btn{
        min-width: 100px;
        padding: 4px 15px;
        border-radius: 2px;
        overflow: hidden;
        border: 1px solid transparent;
        background-color: #f5f5f5;
        color: #333;
        display: flex;
        min-height: 70px;
        align-items: center;
        .sku-img-box{
          height: 70px;
          margin-right: 10px;
        }
        .sku-img{
          height: 100%;
          width: 70px;
          object-fit: contain;
        }
        .sku-value-text {
          font-size: 28px;
        }
      }
      .big-sku-btn{
        flex-direction: column;
        padding: 0;
        .sku-img-box{
          height: 160px;
          margin-right: 0;
        }
        .sku-img{
          width: 160px;
          height: 100%;
          object-fit: contain;
          margin-right: 0;
        }
        .sku-value-text{
          display: inline-block;
          padding: 10px;
        }
      }
      .sku-btn[select="1"]{
        border-color: var(--default_color);
        color: var(--default_color);
        background-color: #fff;
      }
      .sold-out-mark{
        position: absolute;
        top: -10px;
        right: -8px;
        background-color: #cacaca;
        color: #fff;
        border-radius: 25px;
        // line-height: 25px;
        padding: 4px 8px;
        font-size: 16px;
        overflow: hidden;
      }
    }
  }
}
</style>
